import { Box } from "@mui/material";
import { Link as RouterLink } from "react-router";
import type { CSSProperties } from "react";

type Variant = "horizontal" | "mark";
type Size = "sm" | "md" | "lg" | "xl";

const SIZE_MAP: Record<Size, { wordmarkPx: number; markPx: number; gap: number }> = {
  sm: { wordmarkPx: 20, markPx: 22, gap: 8 },
  md: { wordmarkPx: 28, markPx: 32, gap: 10 },
  lg: { wordmarkPx: 44, markPx: 52, gap: 14 },
  xl: { wordmarkPx: 64, markPx: 72, gap: 18 },
};

interface LogoProps {
  variant?: Variant;
  size?: Size;
  color?: string;
  to?: string;
  href?: string;
  ariaLabel?: string;
  style?: CSSProperties;
}

/**
 * Corian Park logo — bulldozer mark + wordmark.
 * Uses the shipped /favicon.svg as the mark so there's a single source of truth
 * between the favicon, header, and footer.
 */
export function Logo({
  variant = "horizontal",
  size = "md",
  color,
  to,
  href,
  ariaLabel = "Corian Park",
  style,
}: LogoProps) {
  const { wordmarkPx, markPx, gap } = SIZE_MAP[size];

  const mark = (
    <Box
      component="img"
      src="/favicon.svg"
      alt=""
      aria-hidden
      sx={{
        height: markPx,
        width: "auto",
        display: "block",
        filter: color ? `drop-shadow(0 0 0 ${color})` : undefined,
      }}
    />
  );

  const inner = (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: `${gap}px` }}>
      {mark}
      {variant === "horizontal" && (
        <Box
          component="span"
          sx={{
            fontFamily: "'Bebas Neue', Impact, 'Arial Narrow', sans-serif",
            fontSize: wordmarkPx,
            fontWeight: 400,
            lineHeight: 1,
            letterSpacing: 2,
            color: color ?? "primary.main",
            userSelect: "none",
          }}
        >
          CORIAN PARK
        </Box>
      )}
    </Box>
  );

  const baseStyle: CSSProperties = {
    display: "inline-flex",
    textDecoration: "none",
    ...style,
  };

  if (to) {
    return (
      <Box component={RouterLink} to={to} aria-label={ariaLabel} sx={baseStyle}>
        {inner}
      </Box>
    );
  }
  if (href) {
    return (
      <Box component="a" href={href} aria-label={ariaLabel} sx={baseStyle}>
        {inner}
      </Box>
    );
  }
  return (
    <Box aria-label={ariaLabel} sx={baseStyle}>
      {inner}
    </Box>
  );
}
